import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  ArrowDownCircleIcon,
  ArrowPathIcon,
  ArrowUpCircleIcon,
} from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Listbox,
  Transition,
} from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import CorrectIcon from "../../Assets/Icons/Icons";
import PendingIcon from "../../Assets/Icons/PendingIcon";
import PendingApproval from "../../Assets/Icons/PendingApproval";
import RequestForm from "../Requests/Forms/RequestForm";
import { toast, ToastContainer } from "react-toastify";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  ExpireLink,
  GetDepReserveAmount,
  GetReserveAmount,
} from "./API/RequestsServices";
import PaymentSummaryCard from "./Reports/PaymentSummaryCard";
import DonutStatusCard from "./Reports/DonutStatusCard";
import DonutBoard from "./Reports/DonutStatusCard";
import DownloadCreatedReport from "./Reports/DownloadCreatedReport";
import Loader from "../../../LoadingIcons/Loader";
import CollectionsByMonthBoard from "./Reports/GetPaidCollectionsReports";
import DepartmentMonthLeaderboard from "./Reports/GetDepartmentMonthSummary";
import TransactionHistory from "./Components/TransactionHistory";

/* ------------------ helpers ------------------ */

// trim only trailing spaces (keeps internal spaces & caret position UX)
const rtrim = (s) => (s ?? "").replace(/\s+$/g, "");

// sanitize bahrain mobile digits helper
const sanitizeDigits = (v) => {
  let s = String(v ?? "").replace(/\D/g, "");
  if (s.startsWith("973")) s = s.slice(3);
  return s.slice(0, 8);
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* ------------------ constants ------------------ */

const statuses = {
  Paid: "text-green-700 bg-green-50 ring-green-600/20",
  Pending: "text-orange-700 bg-yellow-100 ring-orange-600/20",
  Overdue: "text-red-700 bg-red-50 ring-red-600/20",
  PendingApproval: "text-blue-700 bg-blue-100 ring-blue-600/20",
  Expired: "text-red-700 bg-red-100 ring-red-600/20",
};

const REPORTS = [
  { key: "department", label: "Digital & Department Performance" },
  { key: "collections", label: "Collections by Month" },
  { key: "status", label: "Payment Status" },
];

const statusOptions = [
  { value: "", label: "All Statuses" },
  { value: "Paid", label: "✅ Paid" },
  { value: "PendingApproval", label: "⏳ Pending Approval" },
  { value: "Pending", label: "🕒 Pending" },
  { value: "Expired", label: "❌ Expired" },
];

/* ------------------ component ------------------ */

const RequestsLists = () => {
  /* ----- UI + filters ----- */
  const [selectedView, setSelectedView] = useState("Transactions");
  const [selectedReport, setSelectedReport] = useState("department");

  const [isLoading, setLoading] = useState(false);
  const [isFormLoading, setFormLoading] = useState(false);
  const [isGeneratedLink, setGeneratedLink] = useState(false);
  const [ShortLink, setShortLink] = useState("");
  const [isViewLoading, setViewLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenNewRequest, setIsModalOpenOpenNewRequest] = useState(false);
  const [isPendingApprovalLoading, setPendingApprovalLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("details"); // "details" | "history"

  /* ----- data + paging ----- */
  const [days, setDays] = useState([]); // grouped but we use flat map
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  /* ----- search/filter ----- */
  const [searchString, setSearchString] = useState("");
  const [filteredStatus, setFilteredStatus] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(
    statusOptions.find((s) => s.value === filteredStatus) || statusOptions[0]
  );

  /* ----- selected transaction ----- */
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  /* ----- request creation form states ----- */
  const allRequestTypes = [
    { label: "Policy", value: "Policy" },
    { label: "General Payment", value: "General Payment" },
    { label: "Claim Deductible", value: "deductible" },
    { label: "Claim Depreciation", value: "depreciation" },
    { label: "Recurring Payment (Active Policy)", value: "ActivePolicyRecurring" },
  ];
  const allowedRequestTypes = JSON.parse(
    sessionStorage.getItem("requestTypeAccess") || "[]"
  );

  const filteredOptions = allRequestTypes.filter((option) =>
    allowedRequestTypes.includes(option.value)
  );

  const [selectedOption, setSelectedOption] = useState("");
  useEffect(() => {
    if (filteredOptions.length > 0) {
      if (!filteredOptions.some((opt) => opt.value === selectedOption)) {
        setSelectedOption(filteredOptions[0].value);
      }
    } else {
      setSelectedOption("");
    }
  }, [filteredOptions, selectedOption]);

  const [selectedProduct, setProductOption] = useState("Motor");

  const [selectedForm, setSelectedForm] = useState("requestDetails");
  const [quoteNo, setQuoteNo] = useState("");
  const [debouncedQuoteNo, setDebouncedQuoteNo] = useState(quoteNo);
  const [hasReserve, setHasReserve] = useState(false);

  const [PolicyDetails, setPolicyDetails] = useState({
    Quote: "",
    ProductName: "",
    TotalAmount: 0,
    Make: "",
    Model: "",
    Plate: "",
    desc: "",
  });

  const [CustomerDetails, setCustomerDetails] = useState({
    FirstName: "",
    lastName: "",
    email: "",
    phone: "",
    cpr: "",
    expiry: "",
  });

  /* ----- effects ----- */

  useEffect(() => {
    const usernameSession = sessionStorage.getItem("username");
    if (usernameSession) {
      fetchPaymentRequests(); // initial load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuoteNo(quoteNo), 500);
    return () => clearTimeout(timer);
  }, [quoteNo]);

  useEffect(() => {
    if (
      selectedOption === "Policy" ||
      selectedOption === "ForRecurring" ||
      selectedOption === "ActivePolicyRecurring"
    ) {
      fetchQuoteDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuoteNo]);

  /* ----- handlers ----- */

  const handleSelectionChange = (e) => setSelectedOption(e.target.value);
  const handleProductChange = (e) => setProductOption(e.target.value);

  const handleQuoteChange = (event) => {
    setHasReserve(false);
    setQuoteNo(rtrim(event.target.value));
    if (
      selectedOption === "deductible" ||
      selectedOption === "depreciation"
    ) {
      if (selectedOption === "deductible") {
        GetReserveAmount(
          event.target.value,
          "DUDC-RECV",
          setFormLoading,
          setPolicyDetails,
          setCustomerDetails,
          setHasReserve,
          toast
        );
      } else {
        GetDepReserveAmount(
          event.target.value,
          "DEPRE-RECV",
          setFormLoading,
          setPolicyDetails,
          setCustomerDetails,
          setHasReserve,
          toast
        );
      }
    }
  };

  const OpenNewRequest = () => setIsModalOpenOpenNewRequest(true);
  const closeNewRequestModal = () => setIsModalOpenOpenNewRequest(false);

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setGeneratedLink(false);
    setShortLink("");
    setSelectedTransaction(null);
  };

  const handleNextPage = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  const handleSearchClick = () => {
    setCurrentPage(1);
    fetchPaymentRequests();
  };

  const handleSearchChange = (event) => setSearchString(event.target.value);

  const handleExpire = (id, token) => {
    ExpireLink(id, token, toast, fetchPaymentRequests, setIsModalOpen);
  };

  const sanitize = (v) => sanitizeDigits(v);

  // ISO of next local midnight (after date-only string)
  function nextMidnightUtcIso(dateOnly) {
    if (!dateOnly) return null;
    const [y, m, d] = dateOnly.split("-").map(Number);
    const localMidnight = new Date(y, m - 1, d, 0, 0, 0, 0);
    const nextLocalMidnight = new Date(localMidnight);
    nextLocalMidnight.setDate(nextLocalMidnight.getDate() + 1);
    const utcMillis =
      nextLocalMidnight.getTime() -
      nextLocalMidnight.getTimezoneOffset() * 60000;
    return new Date(utcMillis).toISOString();
  }

  /* ----- API calls ----- */

  const GenerateLink = async (id) => {
    setViewLoading(true);
    const url = process.env.REACT_APP_PAYMENT_API + "Requests/GenerateLink";
    const data = { id };
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to fetch payment requests");
      const result = await response.json();
      setGeneratedLink(true);
      setShortLink(result.link);
      setViewLoading(false);
      return result;
    } catch (error) {
      setViewLoading(false);
      console.error("Error fetching payment requests:", error);
    }
  };

  const ReApprovePolicy = async (id, Channel) => {
    const url = process.env.REACT_APP_PAYMENT_API + "Payment/ReApprovePolicy";
    const data = { id, Channel };
    try {
      setPendingApprovalLoading(true);
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorDetails = await response.json();
        toast.error(errorDetails.Message, {
          style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
        });
        setPendingApprovalLoading(false);
        return errorDetails;
      }
      const result = await response.json();
      setPendingApprovalLoading(false);
      return result;
    } catch (error) {
      toast.error(error.Message, {
        style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
      });
      setPendingApprovalLoading(false);
      console.error("Error fetching payment requests:", error);
    }
  };

  const handleReApprove = async (id, channel) => {
    await ReApprovePolicy(id, channel);
    setIsModalOpen(false);
    fetchPaymentRequests();
  };

  const fetchQuoteDetails = async () => {
    if (debouncedQuoteNo.trim() === "") return;
    const url = process.env.REACT_APP_PAYMENT_API + "Policy/GetPolicyDetails";
    const data = { Policy: quoteNo?.trim() };
    try {
      setFormLoading(true);
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to fetch payment requests");
      const result = await response.json();

      if (selectedOption !== "ActivePolicyRecurring") {
        if (result.policyStatus === "01" || result.policyStatus === "12") {
          if (result.policySubStatus === "04" || result.policySubStatus === "Bind") {
            setPolicyDetails({
              Quote: result.quotationNumber,
              ProductName: result.productName,
              TotalAmount: result.totalAmount,
              Make: result.make,
              Model: result.model,
              Plate: result.registrationNumber,
              desc:
                result.productCode === "0101"
                  ? `${result.make} ${result.model} Plate Number: ${result.registrationNumber}`
                  : "",
            });

            const endOfMonth = new Date(
              new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).setHours(12)
            )
              .toISOString()
              .split("T")[0];

            const phoneNo =
              result.customerDetails.phoneNumber
                ?.replace(/\D/g, "")
                .replace(/^973/, "")
                .slice(0, 8) || "";

            setCustomerDetails({
              FirstName: result.customerDetails.firstName,
              lastName: result.customerDetails.lastName,
              email: result.customerDetails.email ?? "",
              phone: phoneNo ?? "",
              cpr: result.customerDetails.cpr,
              code: result.customerDetails.code,
              expiry: endOfMonth,
            });
          } else {
            toast.error("Policy Sub-Status must be Bind", {
              style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
            });
            setPolicyDetails({
              Quote: "",
              ProductName: "",
              TotalAmount: "",
              Make: "",
              Model: "",
              Plate: "",
              desc: "",
            });
            setCustomerDetails({
              FirstName: "",
              lastName: "",
              email: "",
              phone: "",
            });
          }
        } else {
          toast.error("Policy is already active", {
            style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
          });
          setPolicyDetails({
            Quote: "",
            ProductName: "",
            TotalAmount: "",
            Make: "",
            Model: "",
            Plate: "",
            desc: "",
          });
          setCustomerDetails({
            FirstName: "",
            lastName: "",
            email: "",
            phone: "",
          });
        }
      } else {
        if (result.policyStatus === "05") {
          setPolicyDetails({
            Quote: result.quotationNumber,
            ProductName: result.productName,
            TotalAmount: result.paymentAmountBhd,
            Make: result.make,
            Model: result.model,
            Plate: result.registrationNumber,
            desc:
              result.productCode === "0101"
                ? `${result.make} ${result.model} Plate Number: ${result.registrationNumber}`
                : "",
          });

          setCustomerDetails({
            FirstName: result.customerDetails.firstName,
            lastName: result.customerDetails.lastName,
            email: result.customerDetails.email ?? "",
            phone: result.customerDetails.phoneNumber ?? "",
            cpr: result.customerDetails.cpr,
            code: result.customerDetails.code,
            expiry: "",
          });
        } else {
          toast.error("Policy is not active", {
            style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
          });
          setPolicyDetails({
            Quote: "",
            ProductName: "",
            TotalAmount: "",
            Make: "",
            Model: "",
            Plate: "",
            desc: "",
          });
          setCustomerDetails({
            FirstName: "",
            lastName: "",
            email: "",
            phone: "",
          });
        }
      }

      setFormLoading(false);
    } catch (error) {
      toast.error(error.message, {
        style: { background: "#ffcccc", color: "black", fontWeight: "bold" },
      });
      setFormLoading(false);
      console.error("Error fetching payment requests:", error);
    }
  };

  const fetchPaymentRequests = async () => {
    const url = process.env.REACT_APP_PAYMENT_API + "Requests/GetPaymentRequests";
    const data = {
      SearchString: searchString,
      CurrentPage: currentPage,
      RowsToReturn: itemsPerPage,
      Token: sessionStorage.getItem("token"),
      Status: filteredStatus,
    };

    try {
      setLoading(true);
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error("Failed to fetch payment requests");

      const result = await response.json();

      if (result.paymentRequests && Array.isArray(result.paymentRequests)) {
        setTotalPages(result.pagination.totalPages);
        setTotalItems(result.pagination.totalRecords);

        const sortedTransactions = result.paymentRequests.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        
        const transactions = sortedTransactions.map((request) => ({
          id: request.id,
          amount: request.amount,
          status:
            request.isPaid && request.isSuccess
              ? "Paid"
              : request.isPaid && !request.isSuccess
              ? "Pending Approval"
              : !request.isPaid && !request.expiryDate
              ? "Pending"
              : !request.isPaid && new Date() > new Date(request.expiryDate)
              ? "Expired"
              : "Pending",
          client: `${request.customerInfo.firstName} ${request.customerInfo.lastName}`,
          description: request.payment_desc,
          href: `/transactions/${request.id}`,
          invoiceNumber: request.quoteNumber,
          date: request.date,
          icon: null,
          createdDate: request.createdDate,
          mobile: request.customerInfo.phoneNumber,
          email: request.customerInfo.email,
          userName: request.userName,
          productName: request.productName,
          requestType:
            request.requestType === "ForRecurring"
              ? "For Recurring (First Time)"
              : request.requestType,
          policyNumber: request.policyNumber,
          expiryDate: request.expiryDate,
          approvalResult: request.approvalResult,
          authCode: request.transaction?.authCode ?? "",
          chargeId: request.transaction?.chargeId ?? "",
          gateway: request.transaction?.gateway ?? "",
          paymentAttempts: request.paymentAttempts
        }));

        setDays([{ transactions }]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching payment requests:", error);
    }
  };

  const currentTransactions = days.flatMap((day) => day.transactions);

  /* ----- forms for the request modal (unchanged, with subtle trailing-trim) ----- */

  // Request Details
 // === Request Details (no outer borders) ===
const requestDetailsForm = (
  <div className="p-2">
    <div className="bg-transparent p-0 sm:p-0">
      {/* Policy / Claim */}
      <label className="block text-sm font-medium text-gray-900">
        {selectedOption === "deductible" || selectedOption === "depreciation"
          ? "Claim Number"
          : "Policy / Quote"}
      </label>
      <div className="mt-2">
        <input
          id="policy"
          name="policy"
          type="text"
          placeholder="Q4255555555503-01"
          value={quoteNo}
          onChange={handleQuoteChange}
          className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Request type */}
      <label className="mt-4 block text-sm font-medium text-gray-900">
        Request Type
      </label>
      {filteredOptions.length > 0 ? (
        <div className="mt-2 relative">
          <select
            id="location"
            name="location"
            value={selectedOption}
            onChange={handleSelectionChange}
            className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {filteredOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
          />
        </div>
      ) : (
        <p className="mt-2 text-sm text-red-600">
          You don’t have access to any request types.
        </p>
      )}

      {/* Product (only for General Payment) */}
      {selectedOption === "General Payment" && (
        <>
          <label className="mt-4 block text-sm font-medium text-gray-900">
            Product Type
          </label>
          <div className="mt-2 relative">
            <select
              value={selectedProduct}
              onChange={handleProductChange}
              id="product"
              name="product"
              className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option>Motor</option>
              <option>Travel</option>
              <option>Home</option>
              <option>Domestic helper</option>
              <option>Medical</option>
              <option>Others</option>
            </select>
            <ChevronDownIcon
              aria-hidden="true"
              className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            />
          </div>
        </>
      )}

      {/* Amount */}
      <label className="mt-4 block text-sm font-medium text-gray-900">
        Amount
      </label>
      <div className="mt-2">
        <input
          id="amount"
          name="amount"
          type="number"
          inputMode="decimal"
          step="0.001"
          placeholder="0 BHD"
          value={PolicyDetails.TotalAmount}
          onChange={(e) =>
            setPolicyDetails((prev) => ({
              ...prev,
              TotalAmount: rtrim(e.target.value),
            }))
          }
          className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Description */}
      <label className="mt-4 block text-sm font-medium text-gray-900">
        Description
      </label>
      <div className="mt-2">
        <textarea
          id="Description"
          name="Description"
          placeholder="Add a short description (optional)"
          rows={3}
          value={PolicyDetails.desc}
          onChange={(e) =>
            setPolicyDetails((prev) => ({ ...prev, desc: e.target.value }))
          }
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </div>
  </div>
);


  // Customer Details
// === Customer Details (no outer borders) ===
const customerDetailsForm = (
  <div className="p-2">
    <div className="bg-transparent p-0 sm:p-0">
      {/* Name */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Client First Name
          </label>
          <input
            id="fname"
            name="fname"
            type="text"
            placeholder="Jon"
            value={CustomerDetails.FirstName}
            onChange={(e) =>
              setCustomerDetails((prev) => ({
                ...prev,
                FirstName: rtrim(e.target.value),
              }))
            }
            className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900">
            Client Last Name
          </label>
          <input
            id="lname"
            name="lname"
            type="text"
            placeholder="Seena"
            value={CustomerDetails.lastName}
            onChange={(e) =>
              setCustomerDetails((prev) => ({
                ...prev,
                lastName: rtrim(e.target.value),
              }))
            }
            className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* CPR (conditional) */}
      {(selectedOption === "General Payment" ||
        selectedOption === "deductible" ||
        selectedOption === "depreciation") && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-900">
            CPR / Unique reference{" "}
            <span className="ml-1 font-normal text-gray-500">
              (must be unique per customer)
            </span>
          </label>
          <input
            id="cpr"
            name="cpr"
            type="number"
            value={CustomerDetails.cpr}
            onChange={(e) =>
              setCustomerDetails((prev) => ({ ...prev, cpr: rtrim(e.target.value) }))
            }
            className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      )}

      {/* Email & Phone */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Client Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="email@company.com"
            value={CustomerDetails.email ?? ""}
            onChange={(e) =>
              setCustomerDetails((prev) => ({ ...prev, email: rtrim(e.target.value) }))
            }
            className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900">
            Client Mobile
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            placeholder="Enter Bahraini number"
            value={sanitize(CustomerDetails?.phone)}
            onChange={(e) => {
              let input = e.target.value;
              input = input.replace(/\D/g, "");
              if (input.startsWith("973")) input = input.slice(3);
              input = input.slice(0, 8);
              setCustomerDetails((prev) => ({ ...prev, phone: input }));
            }}
            className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="mt-1 text-xs text-gray-500">8 digits (no country code).</p>
        </div>
      </div>

      {/* Expiry */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-900">
          Expiry Date
        </label>
        <input
          id="date"
          name="date"
          type="date"
          value={CustomerDetails.expiry}
          min={new Date().toISOString().split("T")[0]}
          onChange={(e) => {
            const newExpiry = e.target.value;
            setCustomerDetails((prev) => ({ ...prev, expiry: newExpiry }));
          }}
          className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </div>
  </div>
);


  /* ----- UI RENDER ----- */

  if (selectedView === "Reports") {
    return (
      <div className="mt-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">Reports</h2>
              <span className="hidden sm:inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
                Insight overview
              </span>
            </div>
            <button
              onClick={() => setSelectedView("Transactions")}
              className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
            >
              ← Back to Transactions
            </button>
          </div>

          {/* Report Switcher */}
          <div
            role="tablist"
            aria-label="Report type"
            className="mb-6 flex w-full gap-1 rounded-lg bg-gray-50 p-1"
          >
            {REPORTS.map((r) => {
              const selected = selectedReport === r.key;
              return (
                <button
                  key={r.key}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setSelectedReport(r.key)}
                  className={classNames(
                    "w-full rounded-md px-3 py-2 text-sm transition",
                    selected ? "bg-white text-gray-900 shadow" : "text-gray-600 hover:text-gray-900"
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Report Container */}
          <div className="rounded-2xl shadow-sm">
            {selectedReport === "status" && <DonutBoard toast={toast} />}
            {selectedReport === "collections" && <CollectionsByMonthBoard toast={toast} />}
            {selectedReport === "department" && <DepartmentMonthLeaderboard toast={toast} />}
          </div>
        </div>
      </div>
    );
  }

  /* --------- TRANSACTIONS VIEW --------- */
  return (
    <div className="py-4">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Toolbar */}
        <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Transactions</h2>
            <p className="mt-1 text-sm text-gray-500">
              Create and manage payment requests. Use search and filters to narrow results.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedView("Reports")}
              className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
            >
              View Reports
            </button>
            <button
              onClick={OpenNewRequest}
              className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            >
              + New Request
            </button>
          </div>
        </div>

      {/* === Toolbar / Search & Filters — REPLACEMENT (sleek/pro) === */}
{/* === Compact Toolbar / Filters (replacement) === */}
<div className="mb-5">
  <div className="rounded-xl border border-gray-200 bg-white/90 backdrop-blur px-3 py-3 shadow-sm">
    {/* Constrain inner width so it never feels too wide */}
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {/* Left: Status chips + label */}
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold text-gray-900">Filters</h3>

          {/* Chips (desktop/tablet) */}
          <div className="hidden sm:flex flex-wrap items-center gap-1.5">
            {statusOptions.map((s) => {
              const active = selectedStatus?.value === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => { setSelectedStatus(s); setFilteredStatus(s.value); }}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition",
                    active
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  ].join(" ")}
                  title={s.label}
                >
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Mobile select */}
          <div className="sm:hidden">
            <label htmlFor="mobile-status" className="sr-only">Status</label>
            <select
              id="mobile-status"
              className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={selectedStatus?.value ?? ""}
              onChange={(e) => {
                const next = statusOptions.find(x => x.value === e.target.value) || statusOptions[0];
                setSelectedStatus(next); setFilteredStatus(next.value);
              }}
            >
              {statusOptions.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Right: Search + action (don’t allow to grow full width) */}
        <div className="flex w-full items-center gap-2 sm:justify-end lg:w-auto">
          <div className="relative w-full sm:w-[320px]">
            <label className="sr-only" htmlFor="search-transactions">Search transactions</label>
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
            </svg>
            <input
              id="search-transactions"
              type="text"
              placeholder="Search client, reference, amount…"
              value={searchString}
              onChange={handleSearchChange}
              className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <button
            onClick={handleSearchClick}
            className="h-10 shrink-0 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
          >
            Search
          </button>
        </div>
      </div>
    </div>
  </div>
</div>


{/* === Results — REPLACEMENT (refined data grid) === */}
{/* === Results — list-style table that matches the screenshot === */}
{isLoading ? (
  <Loader variant="overlay" size={28} label="Fetching Data..." />
) : (
  <div className="overflow-hidden rounded-2xl  bg-white shadow-sm">
    {/* Header line (optional) */}
    <div className="hidden md:flex items-center justify-between px-6 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b bg-gray-50">
      <span className="w-48">Amount</span>
      <span className="flex-1">Client</span>
      <span className="w-72 text-right">Actions</span>
    </div>

    {/* Rows */}
    <ul role="list" className="divide-y divide-gray-200">
      {currentTransactions.map((t) => {
        const statusKey = t.status === "Pending Approval" ? "PendingApproval" : t.status;

        // pill colors tuned for the screenshot look
        const badge =
          statusKey === "Paid"
            ? "text-green-700 bg-green-100 ring-green-600/20"
            : statusKey === "PendingApproval"
            ? "text-blue-700 bg-blue-100 ring-blue-600/20"
            : statusKey === "Expired"
            ? "text-amber-800 bg-red-100 ring-amber-600/20"
            : "text-amber-800 bg-amber-100 ring-amber-600/20"; // Pending

        return (
          <li key={t.id} className="px-6 py-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[12rem_1fr_18rem] md:items-start">
              {/* LEFT: Amount + Status */}
              <div className="flex items-center gap-3 md:justify-start">
                <div className="text-[15px] font-semibold text-slate-900">
                  {t.amount} <span className="font-normal text-slate-500">BHD</span>
                </div>
                <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${badge}`}>
                  {t.status}
                </span>
              </div>

              {/* MIDDLE: Client + type (matches “Policy” line in screenshot) */}
              <div className="min-w-0">
                <div className="truncate font-semibold text-sm text-slate-900 uppercase">
                  {t.client}
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  {t.requestType || "Policy"}
                </div>
              </div>

              {/* RIGHT: View link + meta (Reference / Created By) */}
              <div className="md:text-right">
                <button
                  onClick={() => handleViewTransaction(t)}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  View transaction
                </button>

                <div className="mt-2 space-y-1 text-xs text-slate-500">
                  <div>
                    Reference{" "}
                    <span className="font-medium text-slate-700">
                      #{t.policyNumber === "" ? t.invoiceNumber : t.policyNumber ?? t.invoiceNumber}
                    </span>
                  </div>
                  <div>
                    Created By{" "}
                    <span className="font-semibold text-slate-700">
                      {t.userName
                        ?.split(".")
                        .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
                        .join(" ")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </li>
        );
      })}

      {currentTransactions.length === 0 && (
        <li className="px-6 py-16">
          <div className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <svg className="h-5 w-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">No transactions found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters.</p>
          </div>
        </li>
      )}
    </ul>
  </div>
)}


{/* === Pagination (soft UI) === */}
<div className="mt-4 flex items-center justify-between">
  <button
    onClick={handlePreviousPage}
    disabled={currentPage === 1}
    className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-50"
  >
    Previous
  </button>
  <span className="text-sm text-gray-700">
    Page {currentPage} of {totalPages || 1}
  </span>
  <button
    onClick={handleNextPage}
    disabled={currentPage === totalPages || totalPages === 0}
    className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-50"
  >
    Next
  </button>
</div>



      
      
      
      </div>

      {/* Transaction Modal */}
  {isModalOpen && selectedTransaction && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
      {/* Header */}
      <div className="flex items-center gap-x-4 border-b border-gray-100 bg-gray-50 px-6 py-4">
        {selectedTransaction.status === "Paid" ? (
          <CorrectIcon />
        ) : selectedTransaction.status === "Pending Approval" ? (
          <PendingApproval />
        ) : (
          <PendingIcon />
        )}

        <div className="text-sm font-semibold text-gray-900">
          {selectedTransaction.policyNumber === ""
            ? selectedTransaction.invoiceNumber
            : selectedTransaction.policyNumber ?? selectedTransaction.invoiceNumber}
        </div>

        <Menu
          as="div"
          className={classNames(
            "relative ml-auto",
            selectedTransaction.status === "Pending" ? "" : "hidden"
          )}
        >
          <MenuButton className="-m-2.5 block rounded-md p-2.5 text-gray-400 hover:text-gray-500 hover:bg-gray-100">
            <span className="sr-only">Open options</span>
            <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
          </MenuButton>
          <MenuItems
            transition
            className="absolute right-0 z-10 mt-0.5 w-36 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <MenuItem>
              <button
                onClick={() =>
                  handleExpire(selectedTransaction.id, sessionStorage.getItem("token"))
                }
                className="block w-full px-3 py-1 text-left text-sm text-gray-900 data-[focus]:bg-gray-50"
              >
                Expire Request
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>

      {/* Body */}
      <div className="px-6 py-5 text-sm space-y-5">
        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
          <button
            type="button"
            onClick={() => setActiveTab("details")}
            className={classNames(
              "rounded-md px-3 py-1.5 text-xs font-semibold",
              activeTab === "details"
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
            )}
          >
            Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={classNames(
              "rounded-md px-3 py-1.5 text-xs font-semibold",
              activeTab === "history"
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
            )}
          >
            Transaction history
          </button>
        </div>

        {/* DETAILS TAB */}
        {activeTab === "details" && (
          <dl className="divide-y divide-gray-100">
            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Created Date</dt>
              <dd className="text-gray-700">
                <time dateTime={selectedTransaction.createdDate}>
                  {new Date(selectedTransaction.createdDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Amount</dt>
              <dd className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-gray-900">
                  BHD {selectedTransaction.amount}
                </span>
                <span
                  className={classNames(
                    "rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset",
                    statuses[
                      selectedTransaction.status === "Pending Approval"
                        ? "PendingApproval"
                        : selectedTransaction.status
                    ]
                  )}
                >
                  {selectedTransaction.status}
                </span>
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Name</dt>
              <dd className="font-medium text-gray-900">
                {selectedTransaction.client}
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Phone Number</dt>
              <dd className="font-medium text-gray-900">
                {selectedTransaction.mobile}
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-gray-900 break-all">
                {selectedTransaction.email}
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between">
              <dt className="text-gray-500">Description</dt>
              <dd className="font-medium text-gray-900 sm:text-right">
                {selectedTransaction.description}
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Product</dt>
              <dd className="font-medium text-gray-900">
                {selectedTransaction.productName}
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Expiry Date</dt>
              <dd className="font-medium text-gray-900">
                {selectedTransaction.expiryDate}
              </dd>
            </div>

            <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
              <dt className="text-gray-500">Approval Result</dt>
              <dd className="font-medium text-gray-900">
                {selectedTransaction.approvalResult}
              </dd>
            </div>

            {(selectedTransaction.status === "Paid" ||
              selectedTransaction.status === "Pending Approval") && (
              <>
                <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <dt className="text-gray-500">Auth Code</dt>
                  <dd className="font-medium text-gray-900">
                    {selectedTransaction.authCode}
                  </dd>
                </div>

                <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <dt className="text-gray-500">Tap Payment ID</dt>
                  <dd className="font-medium text-gray-900 break-all">
                    {selectedTransaction.chargeId}
                  </dd>
                </div>

                <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <dt className="text-gray-500">Gateway</dt>
                  <dd className="font-medium text-gray-900">
                    {selectedTransaction.gateway}
                  </dd>
                </div>
              </>
            )}

            {ShortLink && (
              <div className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                <dt className="text-gray-500">Link</dt>
                <dd className="font-medium text-gray-900 break-all">
                  <a
                    href={ShortLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-500 underline"
                  >
                    {ShortLink}
                  </a>
                </dd>
              </div>
            )}
          </dl>
        )}

        {/* HISTORY TAB */}
        {activeTab === "history" && (
          <section className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Transaction history
            </h3>
            <div className="max-h-72 overflow-y-auto">
              <TransactionHistory transactions={selectedTransaction} />
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="mt-2 flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
          <button
            onClick={closeModal}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
          >
            Close
          </button>

          {selectedTransaction.status === "Pending Approval" ? (
            isPendingApprovalLoading ? (
              <PendingApproval />
            ) : (
              <button
                onClick={() =>
                  handleReApprove(
                    selectedTransaction.id,
                    selectedTransaction.userName
                  )
                }
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
              >
                Approve
              </button>
            )
          ) : (
            <button
              onClick={() => GenerateLink(selectedTransaction.id)}
              className={classNames(
                "rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700",
                selectedTransaction.status === "Paid" ? "hidden" : ""
              )}
            >
              {isViewLoading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Generating...
                </span>
              ) : (
                "Generate link"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
)}



      {/* New Request Modal */}
      {isModalOpenNewRequest &&
        (isFormLoading ? (
          <Loader variant="overlay" size={28} label="Sending request..." />
        ) : (
          <RequestForm
            fetchPaymentRequests={fetchPaymentRequests}
            selectedProduct={selectedProduct}
            GeneralQuote={quoteNo}
            setQuoteNo={setQuoteNo}
            setFormLoading={setFormLoading}
            selectedOption={selectedOption}
            PolicyDetails={PolicyDetails}
            CustomerDetails={CustomerDetails}
            selectedForm={selectedForm}
            setSelectedForm={setSelectedForm}
            requestDetailsForm={requestDetailsForm}
            customerDetailsForm={customerDetailsForm}
            closeNewRequestModal={closeNewRequestModal}
            setPolicyDetails={setPolicyDetails}
            setCustomerDetails={setCustomerDetails}
            setHasReserve={setHasReserve}
          />
        ))}
      <ToastContainer />
    </div>
  );
};

export default RequestsLists;
